/* eslint-disable react/no-multi-comp */
import {Context, createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {waitForTime} from '../../../util/time';

import {defaultValues, defaultValuesToString, placeholderText} from './localization-const';
import {
    fetchLocalizationData,
    getLocalizedComponentHelper,
    getLocalizedString as getLocalizedStringHelper,
} from './localization-helper';
import {
    LocaleContextType,
    LocalePropsType,
    LocalizationConfigType,
    LocalizationDataType,
    LocalizationLibraryType,
    LocalizationType,
    ProviderPropsType,
    RawLocalizationDataType,
} from './localization-type';

export function createLocalization<TranslationKeys extends string, LocaleName extends string>(
    localizationConfig: LocalizationConfigType<TranslationKeys, LocaleName>
): LocalizationLibraryType<TranslationKeys, LocaleName> {
    const {defaultLocaleName, localization: localizationArgument, onUseEffect = () => null} = localizationConfig;
    let previousLocalizationName: LocaleName = defaultLocaleName;

    const localization: LocalizationType<LocaleName, TranslationKeys> = {...localizationArgument};

    // for (const key in localization) {
    //     const localizationData: LocalizationDataType<TranslationKeys> | LocalizationDataLoaderType<TranslationKeys> = localization[key];
    //
    //     localeNameMap[key] = typeof localizationData !== 'function' ? null : localizationData;
    // }

    // const localizationMap: Record<LocaleName, null> = Object.fromEntries<Record<LocaleName, null>>(localeNameList);

    // console.log(localeNameList, localizationMap);

    // const localizationMap: Record<LocaleName, null> = Object
    //     .fromEntries<null>(Object.keys(localization).map((key: LocaleName) => [key, null]))

    const defaultLocalizationData: LocaleContextType<TranslationKeys, LocaleName> = {
        getLocalizedString: String.toString,
        isFetchingLocaleData: typeof localization[defaultLocaleName] === 'function',
        localeName: defaultLocaleName,
        setLocaleName: String.toString,
    };

    const LocaleContext: Context<LocaleContextType<TranslationKeys, LocaleName>> =
        createContext<LocaleContextType<TranslationKeys, LocaleName>>(defaultLocalizationData);

    function LocalizationProvider(props: ProviderPropsType): JSX.Element {
        const {children} = props;

        const [localeName, setLocaleName] = useState<LocaleName>(defaultLocalizationData.localeName);
        const [isFetchingLocaleData, setIsFetchingLocaleData] = useState<boolean>(
            defaultLocalizationData.isFetchingLocaleData
        );

        useEffect(() => {
            const existsLocalizationData: RawLocalizationDataType<TranslationKeys> = localization[localeName];

            // check localization data already exists
            if (typeof existsLocalizationData !== 'function') {
                previousLocalizationName = localeName;
                onUseEffect({isFetchingLocaleData: false, localeName});
                return;
            }

            setIsFetchingLocaleData(true);
            onUseEffect({isFetchingLocaleData: true, localeName: previousLocalizationName});

            // eslint-disable-next-line promise/catch-or-return
            fetchLocalizationData<LocaleName, TranslationKeys>(localeName, localization)
                .then((localizationData: LocalizationDataType<TranslationKeys>) => {
                    // Make sure that React's circle is updated
                    // needed to update async locale
                    return waitForTime(0).then(() => {
                        localization[localeName] = localizationData;
                        previousLocalizationName = localeName;

                        onUseEffect({isFetchingLocaleData: false, localeName});
                    });
                })
                .finally((): void => {
                    setIsFetchingLocaleData(false);
                });
        }, [localeName, setIsFetchingLocaleData]);

        const memoizedSetLocaleName = useCallback(
            (newLocaleName: LocaleName) => {
                if (isFetchingLocaleData) {
                    console.log('do not change locale name while localizing data loading');
                    return;
                }

                setLocaleName(newLocaleName);
            },
            [isFetchingLocaleData]
        );

        const getLocalizedString = useCallback(
            (stringKey: TranslationKeys, valueMap?: Record<string, string>): string => {
                const existsLocalizationData: RawLocalizationDataType<TranslationKeys> = localization[localeName];
                const previousLocalizationData: RawLocalizationDataType<TranslationKeys> =
                    localization[previousLocalizationName];

                const values = Object.assign(defaultValuesToString, valueMap || {});

                if (isFetchingLocaleData && typeof previousLocalizationData !== 'function') {
                    return getLocalizedStringHelper<TranslationKeys>(stringKey, previousLocalizationData, values);
                }

                if (typeof existsLocalizationData !== 'function') {
                    return getLocalizedStringHelper<TranslationKeys>(stringKey, existsLocalizationData, values);
                }

                if (typeof previousLocalizationData !== 'function') {
                    return getLocalizedStringHelper<TranslationKeys>(stringKey, previousLocalizationData, values);
                }

                console.log('There is no localization data, return string');

                return placeholderText;
            },
            [localeName, isFetchingLocaleData]
        );

        const providedData: LocaleContextType<TranslationKeys, LocaleName> = useMemo((): LocaleContextType<
            TranslationKeys,
            LocaleName
        > => {
            return {
                getLocalizedString,
                isFetchingLocaleData,
                localeName,
                setLocaleName: memoizedSetLocaleName,
            };
        }, [localeName, memoizedSetLocaleName, getLocalizedString, isFetchingLocaleData]);

        return <LocaleContext.Provider value={providedData}>{children}</LocaleContext.Provider>;
    }

    function Locale(props: LocalePropsType<TranslationKeys>): JSX.Element {
        const {stringKey, valueMap = {}} = props;

        const values = {...defaultValues, ...valueMap};

        const {localeName} = useContext<LocaleContextType<TranslationKeys, LocaleName>>(LocaleContext);

        const existsLocalizationData: RawLocalizationDataType<TranslationKeys> = localization[localeName];
        const previousLocalizationData: RawLocalizationDataType<TranslationKeys> =
            localization[previousLocalizationName];

        if (typeof existsLocalizationData !== 'function') {
            return <>{getLocalizedComponentHelper<TranslationKeys>(stringKey, existsLocalizationData, values)}</>;
        }

        if (typeof previousLocalizationData !== 'function') {
            return <>{getLocalizedComponentHelper<TranslationKeys>(stringKey, previousLocalizationData, values)}</>;
        }

        console.log('There is no localization data, return jsx');

        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <>{placeholderText}</>;
    }

    function useLocale(): LocaleContextType<TranslationKeys, LocaleName> {
        return useContext<LocaleContextType<TranslationKeys, LocaleName>>(LocaleContext);
    }

    return {Locale, LocalizationProvider, useLocale};
}
